import { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import {
  getAdminCatalogBranches,
  getAdminCatalogSubtypes,
  patchAdminCatalogSubtype,
  type AdminCatalogBranchRow,
  type AdminCatalogSubtypeRow,
} from '../../api';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { subtypeFormToSchema, subtypeSchemaToForm } from './subtypeOptionSchema';
import { useHaptic } from '../../hooks/useHaptic';

type SubtypeRow = AdminCatalogSubtypeRow & { tile_hint: string };

export function ConfiguratorTileHintsPanel() {
  const haptic = useHaptic();
  const [branches, setBranches] = useState<AdminCatalogBranchRow[]>([]);
  const [byBranch, setByBranch] = useState<Record<number, SubtypeRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const br = await getAdminCatalogBranches();
      setBranches(br);
      const map: Record<number, SubtypeRow[]> = {};
      await Promise.all(
        br.map(async (b) => {
          const subs = await getAdminCatalogSubtypes(b.id);
          map[b.id] = subs.map((s) => {
            const schema = subtypeSchemaToForm(s.option_schema || {});
            return { ...s, tile_hint: schema.tile_hint || '' };
          });
        }),
      );
      setByBranch(map);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateHint = (branchId: number, subtypeId: number, tile_hint: string) => {
    setByBranch((prev) => ({
      ...prev,
      [branchId]: (prev[branchId] || []).map((s) => (s.id === subtypeId ? { ...s, tile_hint } : s)),
    }));
  };

  const saveSubtype = async (_branchId: number, sub: SubtypeRow) => {
    haptic.selection();
    setSavingId(sub.id);
    setMessage(null);
    try {
      const schema = subtypeSchemaToForm(sub.option_schema || {});
      const next = subtypeFormToSchema({ ...schema, tile_hint: sub.tile_hint });
      await patchAdminCatalogSubtype(sub.id, { option_schema: next });
      setMessage(`Сохранено: ${sub.title}`);
      haptic.success();
    } catch {
      setMessage(`Ошибка сохранения: ${sub.title}`);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <p className="text-xs py-4 text-center" style={{ color: 'var(--text-3)' }}>Загрузка каталога…</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
        Подпись под названием на плитках «Прямой», «Люверсы» и т.д. в конфигураторе (экран «Выберите тип системы»). Отдельно от
        рулонных кнопок «Открытая / 38 мм».
      </p>
      {branches.map((branch) => {
        const subs = byBranch[branch.id] || [];
        if (!subs.length) return null;
        return (
          <AdminGlassDisclosure
            key={branch.id}
            accent="emerald"
            title={branch.title || branch.slug}
            subtitle={`${subs.length} подтипов · плитки конфигуратора`}
          >
            <div className="space-y-3">
              {subs.map((sub) => (
                <div
                  key={sub.id}
                  className="rounded-xl p-3 space-y-2"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                >
                  <div className="flex justify-between gap-2 items-start">
                    <div className="min-w-0">
                      <p className="text-xs font-bold truncate" style={{ color: 'var(--text-1)' }}>
                        {sub.title}
                      </p>
                      <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                        {sub.slug} · {sub.sku_count} SKU
                      </p>
                    </div>
                    {!sub.is_active && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(252,129,74,0.15)', color: '#FC814A' }}>
                        скрыт
                      </span>
                    )}
                  </div>
                  <label className="block">
                    <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Подпись на плитке (мелкий текст)</span>
                    <input
                      className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                      value={sub.tile_hint}
                      placeholder="например: 42 вар. или от 21 400 ₽"
                      onChange={(e) => updateHint(branch.id, sub.id, e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={savingId === sub.id}
                    onClick={() => void saveSubtype(branch.id, sub)}
                    className="press w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-bold touch-manipulation min-h-[44px]"
                    style={{ background: 'rgba(72,187,120,0.15)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
                  >
                    <Save size={12} /> {savingId === sub.id ? '…' : 'Сохранить плитку'}
                  </button>
                </div>
              ))}
            </div>
          </AdminGlassDisclosure>
        );
      })}
      {message && (
        <p className="text-xs px-1" style={{ color: message.startsWith('Ошибка') ? '#FC814A' : '#9AE6B4' }}>
          {message}
        </p>
      )}
    </div>
  );
}
